/**
 * Tool invocation status resolution + live duration tracking.
 *
 * Status is derived from the AI SDK v6 part `state` when present, and
 * inferred from the payload for persisted/legacy parts whose transient state
 * was stripped (`tool-call` rows loaded from the DB). This is what keeps
 * reloaded history from showing an "Unknown" badge.
 */

import type { ToolPartState, ToolPartWithState } from '../utils/toolInvocations.js';

/** Const-union of display statuses (enum-style). */
export const TOOL_STATUSES = [
  'pending', // input still streaming
  'running', // input complete, executing
  'approval', // awaiting user Allow/Deny
  'responded', // approval answered, execution about to continue
  'done',
  'error',
  'denied',
] as const;
export type ToolStatus = (typeof TOOL_STATUSES)[number];

/** Exhaustive stream-state → display-status map (typed over ToolPartState). */
const STATE_TO_STATUS: Record<ToolPartState, ToolStatus> = {
  'input-streaming': 'pending',
  'input-available': 'running',
  'approval-requested': 'approval',
  'approval-responded': 'responded',
  'output-available': 'done',
  'output-error': 'error',
  'output-denied': 'denied',
};

/** True once a tool invocation can no longer change. */
export function isTerminalStatus(status: ToolStatus): boolean {
  return status === 'done' || status === 'error' || status === 'denied';
}

/** Narrow an untrusted state string into the exhaustive map. */
function statusFromState(state: string | undefined): ToolStatus | null {
  if (state && state in STATE_TO_STATUS) {
    return STATE_TO_STATUS[state as ToolPartState];
  }
  return null;
}

/**
 * Resolve the display status for a tool invocation (call part + optional
 * paired result part). Explicit stream state wins; otherwise infer from the
 * payload so persisted history renders correctly.
 */
export function resolveToolStatus(
  part: ToolPartWithState,
  pairedResult?: ToolPartWithState
): ToolStatus {
  const explicit = statusFromState(pairedResult?.state) ?? statusFromState(part.state);
  if (explicit) return explicit;

  if (part.errorText !== undefined || pairedResult?.errorText !== undefined) return 'error';
  if (part.output !== undefined || pairedResult?.output !== undefined) return 'done';
  // A persisted call with no output: treat as finished rather than pending —
  // nothing about a loaded historical message is still running.
  return 'done';
}

/**
 * Live duration tracking, client-side only. Keyed by toolCallId in a module
 * map so re-renders during streaming accumulate start/end timestamps without
 * extra state. History loaded already-terminal never gets a start timestamp,
 * so it simply shows no duration (we don't persist timings).
 */
const timings = new Map<string, { start: number; end?: number }>();
const MAX_TRACKED_TIMINGS = 500;

export function trackToolTiming(toolCallId: string, status: ToolStatus): number | null {
  if (!toolCallId) return null;
  const now = Date.now();
  let entry = timings.get(toolCallId);

  if (!isTerminalStatus(status)) {
    if (!entry) {
      // Cheap unbounded-growth guard for very long sessions.
      if (timings.size >= MAX_TRACKED_TIMINGS) {
        const oldest = timings.keys().next().value;
        if (oldest !== undefined) timings.delete(oldest);
      }
      entry = { start: now };
      timings.set(toolCallId, entry);
    }
    return null;
  }

  if (!entry) return null; // first seen already terminal (loaded history)
  if (entry.end === undefined) entry.end = now;
  return entry.end - entry.start;
}

/** "0.8s" / "12s" / "1m 05s" */
export function formatDuration(ms: number): string {
  if (ms < 0) return '';
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest.toString().padStart(2, '0')}s`;
}
